import { h, JSX } from 'preact';
import Layer from '../layer';
import FullScreenControls from './fullscreen';
import ResponsiveControls from './responsive';
import MpuControls from './mpu';
import {
  isInterstitialAdUnit,
  isOptinVideoAdUnit
} from '../../../../common/js/adUnit';

import { sendAnalyticsTrack } from '../../../../common/js/TrackAnalyticsHelper';
import { EndScreenOrchestrator } from '../../lib/end-screen/orchestrator';
import { RootStore } from '../../../../common/js/stores/root.store';
import { ControlsStrategy } from '../../../../common/js/stores/controls/default-controls-strategy';
import { ControlsStrategyTypes } from '../../../../common/js/stores/controls/default-controls-strategy';
import EndScreenCTAControls from './endscreen-cta/end-screen-cta-controls';
import { VastIconType } from '../../lib/vast/vast-icon';

interface ColorsConfig {
  primaryColor: string;
  secondaryColor: string;
}

interface CtaConfig {
  useCta: boolean;
  useFadeOut: boolean;
  timeBeforeFadeOut: number;
  reverseTopBar: boolean;
  backgroundColor: string;
  labelColor: string;
  label: string;
  animationDelay: number;
}

interface CloseBtnConfig {
  useCrossCloseButton: boolean;
  showCloseDelay: boolean;
  size: number;
  position?: string;
}

interface ControlsProps {
  props: {
    colors: ColorsConfig;
    cta: CtaConfig;
    adUnit: string;
    elapsedTime: number;
    videoDuration: number;
    isVideoMuted: boolean;
    canInstallIcon?: boolean;
    showRecommendation: boolean;
    closeBtn: CloseBtnConfig;
    isRewardVideo: boolean;
    isRewardOnLaunch: boolean;
    video: {
      billedAfter: number;
    };
    confirmSkip: {
      enabled: boolean;
    };
    vastIcon?: VastIconType;
    onOptout: () => void;
    onToggleExpand: (isExpanded: boolean) => void;
    onClickVastIcon?: (url: string) => void;
  };
  onClickVideo: (e: Event) => void;
  onTouchStartVideo: (e: Event) => void;
  setLastClickTime: () => void;
  lastClickTime: number;
  closingScheduledIn?: number;
  clickCloseTime?: number;
  onToggleMute: (e: Event) => void;
  attemptClose: (e: MouseEvent) => void;
  onClick: (source: string, e?: MouseEvent) => void;
  attemptForceClose: (e: MouseEvent) => void;
  showEndScreenClose: boolean;
  skOverlayIsOpened: boolean;
}

// Choose the controls corresponding to the ad unit and ad expanded state
export default function Controls({
  props,
  onClickVideo,
  onTouchStartVideo,
  setLastClickTime,
  lastClickTime,
  closingScheduledIn,
  clickCloseTime,
  onToggleMute,
  attemptClose,
  onClick,
  attemptForceClose,
  showEndScreenClose,
  skOverlayIsOpened
}: ControlsProps): JSX.Element {
  const { colors, cta, adUnit } = props;

  const isInterstitialAd: boolean = isInterstitialAdUnit(adUnit);
  const isOptinVideoAd: boolean = isOptinVideoAdUnit(adUnit);

  const videoStore = RootStore.getInstance().videoStore;
  const controlsStore: ControlsStrategy = RootStore.getInstance().controlsStore;

  const windowSize = videoStore.windowSize.value;
  const isVertical = windowSize.width < windowSize.height;
  const endScreen = EndScreenOrchestrator.instance.endScreen;

  const isFadeout =
    cta &&
    cta.useCta &&
    cta.useFadeOut &&
    props.elapsedTime - lastClickTime > cta.timeBeforeFadeOut;

  const showRecommendation = props.showRecommendation && props.elapsedTime < 5;

  // TODO: use elapsed time from store
  const showClose = controlsStore.shouldDisplayCloseButton(props.elapsedTime);
  const showCloseDisabledInFullScreen = controlsStore.shouldDisableCloseButton(
    showRecommendation,
    props.elapsedTime
  );

  const endScreenCloseButtonSize =
    (endScreen && endScreen.closeBtn && endScreen.closeBtn.size) || 100;
  const videoCloseButtonSize = props.closeBtn && props.closeBtn.size;
  const closeButtonSize = showEndScreenClose
    ? endScreenCloseButtonSize
    : videoCloseButtonSize;

  if (controlsStore.type === ControlsStrategyTypes.END_SCREEN_CTA) {
    return (
      <Layer dataZone="video" zIndex={102}>
        <EndScreenCTAControls
          onOptout={props.onOptout}
          onCTAClick={(e: MouseEvent) => onClick('endScreen', e)}
        />
      </Layer>
    );
  }

  return (
    <Layer
      dataZone="video"
      onClick={onClickVideo}
      onTouchStart={onTouchStartVideo}
      zIndex={102}
    >
      {controlsStore.type === ControlsStrategyTypes.FULLSCREEN ? (
        <FullScreenControls
          closingScheduledIn={closingScheduledIn}
          isVideoMuted={props.isVideoMuted}
          canInstallIcon={props.canInstallIcon}
          closeButtonSize={closeButtonSize}
          elapsedTime={Math.min(props.videoDuration, props.elapsedTime)}
          remainingTime={Math.max(0, props.videoDuration - props.elapsedTime)}
          showClose={showClose}
          showCloseDisabled={showCloseDisabledInFullScreen}
          showRecommendation={showRecommendation}
          showCloseDelay={props.closeBtn && props.closeBtn.showCloseDelay}
          onToggleMute={(e: Event) => {
            !isFadeout && onToggleMute(e);
            setLastClickTime();
          }}
          onOptout={() => {
            !isFadeout && props.onOptout();
            setLastClickTime();
          }}
          onClose={(e: MouseEvent) => {
            if (!isFadeout) {
              !isInterstitialAd &&
                !isOptinVideoAd &&
                sendAnalyticsTrack(e, 'close');
              attemptClose(e);
            }
            setLastClickTime();
          }}
          onCtaClick={(e: MouseEvent) => {
            !isFadeout && onClick('video', e);
            setLastClickTime();
          }}
          videoDuration={props.videoDuration}
          useCrossCloseButton={
            (cta && cta.useCta) ||
            (props.closeBtn && props.closeBtn.useCrossCloseButton)
          }
          isInterstitialAdUnit={isInterstitialAd || isOptinVideoAd}
          onToggleExpand={(isExpanded: boolean) => {
            !isFadeout && props.onToggleExpand(isExpanded);
            setLastClickTime();
          }}
          onClickVastIcon={(url: string) => {
            !isFadeout && props.onClickVastIcon && props.onClickVastIcon(url);
            setLastClickTime();
          }}
          vastIcon={props.vastIcon}
          cta={cta}
          isFadeout={isFadeout}
          clickCloseTime={clickCloseTime}
          showEndScreenClose={showEndScreenClose}
          skOverlayIsOpened={skOverlayIsOpened}
        />
      ) : (
        ''
      )}
      {controlsStore.type === ControlsStrategyTypes.RESPONSIVE ? (
        <ResponsiveControls
          closingScheduledIn={closingScheduledIn}
          isVideoMuted={props.isVideoMuted}
          elapsedTime={Math.min(props.videoDuration, props.elapsedTime)}
          showClose={showClose}
          onToggleMute={onToggleMute}
          onToggleExpand={(isExpanded: boolean) => {
            props.onToggleExpand(isExpanded);
            setLastClickTime();
          }}
          onOptout={props.onOptout}
          onForceClose={(e: MouseEvent) => {
            if (!isInterstitialAd && !isOptinVideoAd) {
              sendAnalyticsTrack(e, 'force_close');
            }
            attemptForceClose(e);
          }}
          videoDuration={props.videoDuration}
          onClickVastIcon={props.onClickVastIcon}
          vastIcon={props.vastIcon}
          clickCloseTime={clickCloseTime}
          isVertical={isVertical}
        />
      ) : (
        ''
      )}

      {controlsStore.type === ControlsStrategyTypes.MPU ? (
        <MpuControls
          colors={colors}
          onOptout={props.onOptout}
          videoDuration={props.videoDuration}
          elapsedTime={Math.min(props.videoDuration, props.elapsedTime)}
          remainingTime={Math.max(0, props.videoDuration - props.elapsedTime)}
          isVideoMuted={props.isVideoMuted}
          onToggleMute={onToggleMute}
          cta={cta}
          onClickVastIcon={props.onClickVastIcon}
          vastIcon={props.vastIcon}
        />
      ) : (
        ''
      )}
    </Layer>
  );
}
